import React from 'react';
import { createPortal } from 'react-dom';
import './LibraryFilterSheet.css';

export type LibraryFilterSheetProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Clears every filter in the sheet. */
  onReset?: () => void;
  resetDisabled?: boolean;
  /** Footer button, e.g. "Show 97 tickets". Fields apply as they change, so this only closes. */
  resultLabel: string;
  /** Where focus goes on close, normally the Filters button. A tap (or a scripted click) does not
   * focus a button in every browser, so the element focused at open time is only a fallback. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
};

const FOCUSABLE = 'button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Phone bottom sheet that holds a library's filters. Its chrome is the phone Menu/Account
 * sheet (`.topbar-mobile-account-*` in global.css); this adds the Reset control, the
 * results button and the dialog behaviour (Escape, focus, scroll lock).
 */
export default function LibraryFilterSheet({
  open,
  onClose,
  title = 'Filters',
  onReset,
  resetDisabled = false,
  resultLabel,
  returnFocusRef,
  children,
}: LibraryFilterSheetProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocusRefRef = React.useRef(returnFocusRef);
  returnFocusRefRef.current = returnFocusRef;

  React.useEffect(() => {
    if (!open) return undefined;
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const body = document.body;
    const bodyWasLocked = body.classList.contains('modal-open');
    if (!bodyWasLocked) body.classList.add('modal-open');
    panelRef.current?.focus({ preventScroll: true });
    const handleKeyDown = (event: KeyboardEvent) => {
      // A picker inside the sheet (multi-select, stage list) marks the Escape that closes its
      // own menu as handled; only the next Escape closes the sheet.
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      event.preventDefault();
      event.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (!bodyWasLocked) body.classList.remove('modal-open');
      (returnFocusRefRef.current?.current ?? returnFocus)?.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  // Keep Tab inside the sheet while it is open.
  const keepFocusInside = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="topbar-mobile-account-portal library-filter-sheet">
      <div className="topbar-mobile-account-overlay" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        className="topbar-mobile-account-panel library-filter-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={keepFocusInside}
      >
        <div className="topbar-mobile-account-panel__handle" aria-hidden="true" />
        <div className="topbar-mobile-account-panel__header">
          <h2 id={titleId} className="topbar-mobile-account-panel__title">{title}</h2>
          {onReset ? (
            <button
              type="button"
              className="library-filter-sheet__reset"
              onClick={() => {
                onReset();
                // Reset disables itself once nothing is left to clear, which drops focus to the
                // page in some browsers; keep it in the sheet.
                panelRef.current?.focus({ preventScroll: true });
              }}
              disabled={resetDisabled}
            >
              Reset
            </button>
          ) : null}
        </div>
        <div className="topbar-mobile-account-panel__body">{children}</div>
        <div className="library-filter-sheet__footer">
          <button type="button" className="btn btn-primary library-filter-sheet__done" onClick={onClose}>
            {resultLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** A labelled control in the sheet; the label names the control (its `id` is filled in). */
export function LibraryFilterSheetField({
  label,
  children,
}: {
  label: string;
  children: React.ReactElement<{ id?: string }>;
}) {
  const generatedId = React.useId();
  const id = children.props.id ?? generatedId;
  return (
    <div className="topbar-mobile-account-panel__field library-filter-sheet__field">
      <label className="topbar-mobile-account-panel__label" htmlFor={id}>{label}</label>
      {React.cloneElement(children, { id })}
    </div>
  );
}

/**
 * A labelled group in the sheet, for controls that take no `id` (multi-selects, the stage
 * list, a select with its sort-direction button, a row of flag toggles): the label names the
 * group instead.
 */
export function LibraryFilterSheetGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const labelId = React.useId();
  return (
    <div className="topbar-mobile-account-panel__field library-filter-sheet__field" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="topbar-mobile-account-panel__label">{label}</span>
      <div className="library-filter-sheet__group">{children}</div>
    </div>
  );
}
